"use client";

import { useRef, useState } from "react";
import { Popover } from "@/components/ui/atoms/popover";
import { useWhatsNew } from "@/contexts/whats-new-context";

/**
 * La pastille d'état du produit, en zone 1 du header, détachée du mot-marque.
 *
 * # Pourquoi ici, et pas seulement sur la carte d'accueil
 *
 * La carte est le premier écran de tout le monde, mais son affichage n'est
 * gouverné par rien qui ait à voir avec l'information : elle se rejoue à chaque
 * rechargement *pour que le jingle rejoue*, et le seul commutateur qui la
 * gouverne s'appelle « Écran d'accueil au démarrage ». Ce qu'on exprime en le
 * coupant, ce n'est pas « j'ai compris que c'est une bêta », c'est « arrête de
 * me jouer le jingle ». Elle s'efface aussi d'elle-même quand un morceau est
 * déjà chargé — arriver par la fiche d'un morceau, c'est ne jamais la voir.
 *
 * Un fait qui doit rester vrai en permanence ne peut pas dépendre d'un réglage
 * de confort sonore. La carte garde une mention, c'est un cadrage de premier
 * contact ; le porteur du statut est ici.
 *
 * # Pourquoi la zone 1 ne coûte rien
 *
 * Les zones 1 et 3 du header sont en `flex-1`, donc son plancher de largeur
 * vaut `nav + 2 × max(zone1, zone3) + 36`. La zone 3 (« Se connecter » et
 * l'ampoule) fait ~134 px, la zone 1 réduite au mot-marque ~62 px. Cette
 * pastille de ~41 px plus 8 px d'écart la porte à ~111 px, toujours sous 134 :
 * le `max()` ne bouge pas, le plancher reste à 729 px et les 39 px de marge
 * avant le repli à 768 px sont intacts. C'est le seul endroit de la barre où
 * l'on peut ajouter quelque chose sans le payer.
 *
 * En dessous de 420 px, la zone 1 porte aussi la miette de navigation : la
 * pastille s'efface, elle y tronquerait « / Bibliothèque ». C'est le téléphone
 * en portrait, la posture de découverte et non la posture de travail.
 *
 * # Pourquoi elle est cliquable
 *
 * Une pastille muette dit « on sait que c'est imparfait » et s'arrête là : une
 * excuse préventive, qui rend le produit moins crédible et non plus. Ouverte
 * sur une explication et un canal, la même information devient un contrat. Et
 * c'est le point d'entrée de feedback le mieux placé du produit : au moment où
 * quelqu'un se demande « c'est un bug ou c'est moi ? », il regarde justement
 * l'étiquette qui dit « bêta ».
 *
 * # Pourquoi un popover et pas un second déclencheur de « Quoi de neuf »
 *
 * Ce serait deux chemins vers la même modale dans la même barre, ce que
 * `AccountControl` refuse déjà pour les réglages. La pastille répond sur place
 * à la question qu'elle pose, puis délègue par deux liens qui ouvrent la popin
 * sur le bon onglet.
 */

interface BetaBadgeProps {
  /** L'anneau de focus de la barre, partagé avec le reste du header. */
  focusRing: string;
}

export function BetaBadge({ focusRing }: BetaBadgeProps) {
  const { openModal: openWhatsNew } = useWhatsNew();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const openTab = (tab: "news" | "feedback") => {
    setOpen(false);
    openWhatsNew(tab);
  };

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((shown) => !shown)}
        aria-label="Bêta publique, en savoir plus"
        aria-expanded={open}
        aria-haspopup="dialog"
        /* `after:` étend la zone de contact à 32 px de haut sans grandir le
           visuel : la pastille fait 16 px, en dessous des 24 px de WCAG 2.5.8.
           Même procédé que les sous-onglets du Studio. */
        /* `bg-accent-subtle` compose ~rgb(31,26,47) sur `--color-bg-deep`, où
           `text-accent-bright` donne 6,2:1 : largement AA pour du 10 px. Écrit
           en classes et non en style inline, sinon le survol ne peut plus
           reprendre la main sur la couleur. */
        className={`relative shrink-0 rounded-[5px] px-1.5 py-[3px] text-[10px] font-semibold leading-none tracking-[0.08em] bg-accent-subtle text-accent-bright hover:text-white cursor-pointer transition-colors duration-150 after:absolute after:content-[''] after:-inset-y-2 after:inset-x-0 [@media(max-width:419px)]:hidden ${focusRing}`}
      >
        BÊTA
      </button>

      <Popover
        anchorRef={anchorRef}
        open={open}
        onClose={() => setOpen(false)}
        placement="bottom-start"
        offset={8}
        ariaLabel="Virtuosa est en bêta publique"
      >
        <div className="w-[280px] py-1">
          <div className="px-3.5 pt-2.5 pb-1">
            <p className="text-[13px] font-semibold text-text-primary">
              Virtuosa est en bêta publique
            </p>
          </div>

          {/* Trois lignes, dans l'ordre où l'inquiétude vient : qui peut
              entrer, ce que devient ce qu'on confie, ce que ça coûte.

              « En échange » fait le travail : il transforme une liste de
              défauts en terme d'un accord. C'est la différence entre un
              avertissement et une invitation.

              La ligne du milieu est un engagement, pas une formule. Si le
              produit ne peut plus la tenir, elle se réécrit ; elle ne
              s'enlève pas. Le silence sur les données est précisément ce qui
              produit le pire scénario dans la tête de qui se pose la
              question. */}
          <div className="px-3.5 pb-2.5 flex flex-col gap-2">
            <p className="text-[12.5px] leading-[18px] text-text-secondary">
              L&apos;accès est ouvert à tout le monde, sans invitation.
            </p>
            <p className="text-[12.5px] leading-[18px] text-text-secondary">
              Ton compte et tes morceaux sont conservés. Rien ne sera remis à
              zéro.
            </p>
            <p className="text-[12.5px] leading-[18px] text-text-secondary">
              En échange : il reste des bugs, et des écrans changent d&apos;une
              semaine à l&apos;autre.
            </p>
          </div>

          <div
            aria-hidden
            className="h-px my-1"
            style={{ backgroundColor: "var(--color-glass-separator)" }}
          />

          <button
            type="button"
            onClick={() => openTab("news")}
            className="w-full text-left px-3.5 py-2 text-[13px] text-text-secondary hover:text-text-primary hover:bg-white/[0.04] transition-colors cursor-pointer"
          >
            Ce qui vient de changer
          </button>

          <button
            type="button"
            onClick={() => openTab("feedback")}
            className="w-full text-left px-3.5 py-2 text-[13px] text-text-secondary hover:text-text-primary hover:bg-white/[0.04] transition-colors cursor-pointer"
          >
            Signaler un problème
          </button>
        </div>
      </Popover>
    </>
  );
}
